/**
 * Column definitions for Ré's chart-of-accounts proposal card
 * (`CoaProposalCard`) — a read-only `EditableTable` under `useIndexGridShell`
 * (`expandable: false`, `floatingHeader: false`), per COMPONENT_PATTERNS.md
 * "Read-Only Tables".
 *
 * Three columns sized to fit the docked Ré panel at its minimum width
 * (BK_MIN_WIDTH 320 minus the message padding). Names are plain text — never
 * markdown: they are what the button will create, so they render verbatim.
 * Hierarchy shows by indent (and by the number suffix the database derives).
 * Once the accounts exist, the name links to the account page.
 */
import { Link } from "react-router-dom";
import type { ColumnConfig } from "@/components/ui/editable-table";

export type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** One row of the proposal grid: a plan account as previewed (or as created). */
export interface CoaProposalGridRow extends Record<string, unknown> {
  /** The plan-local ref — unique within the plan. */
  id: string;
  account_number: string | null;
  name: string;
  /** 0 = top level; one indent step per parent level. */
  depth: number;
  /** Subtype display name (resolved from the code — never the raw code). */
  subtype_label: string;
  /** Set once the account exists (after Create) — the name becomes a link. */
  account_id: string | null;
}

/** Number 64 + name 128 + subtype 96 = 288: the 320px panel minus its padding. */
export function buildCoaProposalGridColumns(t: TranslateFn, orgBasePath: string): ColumnConfig[] {
  return [
    {
      data: "account_number",
      type: "text",
      header: t("bookkeeper.coaProposal.columns.number"),
      width: 64,
      // Number order IS the hierarchy order (a child extends its parent's number).
      sortable: true,
      readOnly: true,
      cellRenderer: (value) => <span className="font-mono text-xs">{value == null ? "" : String(value)}</span>,
    },
    {
      data: "name",
      type: "text",
      header: t("bookkeeper.coaProposal.columns.name"),
      width: 128,
      sortable: false,
      readOnly: true,
      cellRenderer: (value, row) => {
        const gridRow = row as CoaProposalGridRow;
        const name = String(value ?? "");
        return (
          <span className="block truncate" style={{ paddingLeft: `${gridRow.depth * 0.75}rem` }} title={name}>
            {gridRow.account_id && orgBasePath ? (
              <Link
                to={`${orgBasePath}/accounting/accounts/${gridRow.account_id}`}
                className="text-primary underline underline-offset-2"
              >
                {name}
              </Link>
            ) : (
              name
            )}
          </span>
        );
      },
    },
    {
      data: "subtype_label",
      type: "text",
      header: t("bookkeeper.coaProposal.columns.subtype"),
      width: 96,
      sortable: false,
      readOnly: true,
      cellRenderer: (value, row) => (
        <span
          className={(row as CoaProposalGridRow).depth > 0 ? "block truncate text-muted-foreground" : "block truncate"}
          title={String(value ?? "")}
        >
          {String(value ?? "")}
        </span>
      ),
    },
  ];
}
